/* cart-drawer.js — OnbondCart API + <onbond-cart-drawer> web component */
(function () {
  'use strict';

  const EMAIL_KEY = 'onbond:cart-email';

  function money(cents) {
    const n = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return '$' + n;
  }

  function esc(str) {
    const d = document.createElement('div');
    d.appendChild(document.createTextNode(String(str || '')));
    return d.innerHTML;
  }

  /* ── window.OnbondCart API ─────────────────────────────────────────────── */
  window.OnbondCart = {
    async add({ id, quantity = 1 }) {
      quantity = Math.min(1, quantity);
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity }] })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }
      const existing = await this._getCart();
      const item = existing.items.find(i => i.variant_id === parseInt(id, 10));
      if (item && item.quantity > 1) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 1 })
        });
      }
      await this.refresh();
      return r.json().catch(() => ({}));
    },

    async addFromForm(fd) {
      const id = fd.get('id');
      return this.add({ id, quantity: 1 });
    },

    async change({ id, quantity }) {
      quantity = Math.min(1, Math.max(0, quantity));
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity })
      });
      if (!r.ok) throw new Error('Cart change failed');
      await this.refresh();
      return r.json();
    },

    async _getCart() {
      return fetch('/cart.js').then(r => r.json());
    },

    async refresh() {
      const cart = await this._getCart();
      const count = cart.item_count;
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = '[' + String(count).padStart(2, '0') + ']';
      });
      document.dispatchEvent(new CustomEvent('onbond:cart-updated', { detail: cart }));
      return cart;
    }
  };

  /* ── <onbond-cart-drawer> web component ───────────────────────────────── */
  class OnbondCartDrawer extends HTMLElement {
    connectedCallback() {
      this._overlay = this.querySelector('[data-close]');
      this._lines   = this.querySelector('[data-lines]');
      this._subtotal = this.querySelector('[data-subtotal]');
      this._email   = this.querySelector('[data-email]');
      this._mktOpt  = this.querySelector('[data-marketing-opt]');
      this._checkout = this.querySelector('[data-checkout]');

      if (this._overlay) {
        this.querySelectorAll('[data-close]').forEach(el => {
          el.addEventListener('click', () => this.close());
        });
      }
      if (this._checkout) {
        this._checkout.addEventListener('click', () => this._handleCheckout());
      }
      if (this._email) {
        this._email.addEventListener('input', () => {
          localStorage.setItem(EMAIL_KEY, this._email.value);
        });
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.hasAttribute('hidden')) this.close();
      });

      document.addEventListener('onbond:cart-updated', (e) => {
        this._render(e.detail);
      });

      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) window.OnbondCart.refresh();
      });

      window.OnbondCart.refresh();
    }

    open() {
      this.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      const saved = localStorage.getItem(EMAIL_KEY);
      if (saved && this._email) this._email.value = saved;
    }

    close() {
      this.setAttribute('hidden', '');
      document.body.style.overflow = '';
    }

    _render(cart) {
      if (!this._lines) return;

      if (cart.item_count === 0) {
        this._lines.innerHTML = `
          <li class="onbond-drawer__empty">
            <p class="label-mono">Empty archive</p>
            <p class="font-display" style="font-size:1.5rem;margin:8px 0;">No artifacts yet.</p>
            <p class="label-mono" style="margin-bottom:24px;">Browse the archive and add a piece.</p>
            <a href="/collections/all" class="label-mono onbond-btn onbond-btn--ghost" onclick="document.querySelector('onbond-cart-drawer').close()">Browse the archive</a>
          </li>`;
      } else {
        this._lines.innerHTML = cart.items.map(line => {
          const imgUrl = line.featured_image
            ? line.featured_image.url + '?width=160'
            : '';
          const imgTag = imgUrl
            ? `<img src="${imgUrl}" width="80" height="96" alt="${esc(line.product_title)}" loading="lazy">`
            : '<div style="width:80px;height:96px;background:rgb(var(--color-surface))"></div>';
          const variant = line.variant_title && line.variant_title !== 'Default Title'
            ? `<p class="label-mono">${esc(line.variant_title)}</p>` : '';
          return `
            <li class="onbond-drawer__line" data-line-key="${line.key}">
              ${imgTag}
              <div class="onbond-drawer__line-info">
                <p class="label-mono">${esc(line.vendor)}</p>
                <p class="onbond-drawer__line-title font-display">${esc(line.product_title)}</p>
                ${variant}
                <button class="label-mono onbond-drawer__line-remove" data-remove data-key="${line.key}">Remove</button>
              </div>
              <p class="label-mono onbond-drawer__line-price">${money(line.line_price)}</p>
            </li>`;
        }).join('');

        this._lines.querySelectorAll('[data-remove]').forEach(btn => {
          btn.addEventListener('click', async () => {
            await window.OnbondCart.change({ id: btn.dataset.key, quantity: 0 });
          });
        });
      }

      if (this._subtotal) {
        this._subtotal.textContent = money(cart.total_price);
      }
    }

    _handleCheckout() {
      const email  = this._email ? this._email.value.trim().toLowerCase() : '';
      const opt    = this._mktOpt ? this._mktOpt.checked : false;
      const valid  = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) && email.length <= 255;
      let url = '/checkout';
      if (valid) {
        const u = new URL('/checkout', location.origin);
        u.searchParams.set('checkout[email]', email);
        if (opt) u.searchParams.set('checkout[buyer_accepts_marketing]', '1');
        url = u.pathname + u.search;
      }
      window.open(url, '_blank', 'noopener');
    }
  }

  customElements.define('onbond-cart-drawer', OnbondCartDrawer);

})();
